import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useGoodsSno } from './useGoodsSno';
import { useCallback, useEffect } from 'react';
import { queryKey } from '../constant/queryKey';
import { queryKey as sharedQueryKey } from 'shared/src/constant/queryKey';
import {
  AtomicItemListType,
  goodsService,
  type ScreensReadModel,
  screensService,
} from 'shared/src/api';
import { isNotNil } from 'shared/src/utils';
import { useInstanceId, useSafeTimeout } from 'shared/src/hooks';
import { useFixedCouponSno } from './useFixedCouponSno';
import { useSameMarketRecommendOnly } from './useSameMarketRecommendOnly';
import { isSameMarketProxyComponent } from '../utils/isSameMarketProxyComponent';
import { useConfigs } from 'shared/src/queries';

export const usePreloadQuery = () => {
  const client = useQueryClient();
  const goodsSno = useGoodsSno();
  const setSafeTimeout = useSafeTimeout();
  const fixedCouponSno = useFixedCouponSno();
  const sameMarketRecommendOnly = useSameMarketRecommendOnly();
  const instanceId = useInstanceId();

  useConfigs();

  // 이미 캐시에 데이터가 있으면 prefetch 를 건너뛴다. prefetchQuery 는 stale 이면 재요청하므로,
  // 같은 goods 재진입(warm 서피스)·재마운트 때마다 네트워크가 다시 터지는 걸 막는다.
  // (staleness 무관하게 "데이터 존재 여부"로만 판단 → 재진입 시 요청 burst 제거)
  const prefetchIfAbsent = useCallback(
    (key: QueryKey, queryFn: () => Promise<unknown>): Promise<void> => {
      if (client.getQueryData(key) != null) return Promise.resolve();
      return client.prefetchQuery({ queryKey: key, queryFn });
    },
    [client],
  );

  // 첫 페인트(헤더/가격/커버/첫 섹션)에 실제로 필요한 쿼리만 즉시 prefetch한다.
  // 할인/쿠폰/중간광고는 첫 페인트를 막지 않으므로 secondary 로 미뤄, 무거운 콘텐츠
  // 마운트 commit 과 동시에 터지던 요청 burst(요청별 헤더 인터셉터 churn)를 줄인다.
  const prefetchCritical = useCallback(() => {
    prefetchIfAbsent(queryKey.goodsBasic(goodsSno), () =>
      goodsService.getGoodsBasic(goodsSno),
    );
    prefetchIfAbsent(queryKey.goodsDetail(goodsSno), () =>
      goodsService.getGoodsDetail(goodsSno),
    );
    // 기획전 이벤트 연관상품 off 실험: 타 마켓 연관 추천 광고는 prefetch도 하지 않는다.
    if (!sameMarketRecommendOnly) {
      prefetchIfAbsent(queryKey.goodsMidRelatedRecommend(goodsSno), () =>
        goodsService.getMidRelatedAdGoods(goodsSno),
      );
    }

    prefetchIfAbsent(queryKey.goodsCodi(goodsSno), () =>
      goodsService.getCodiGoods(goodsSno),
    );

    // atomic 이 캐시에 있으면 prefetch 는 건너뛰되(Promise.resolve), 캐시된 데이터로
    // proxy prefetch 는 이어서 수행한다(각 proxy 도 존재 시 스킵).
    prefetchIfAbsent(queryKey.goodsAtomicComponents(goodsSno), () =>
      screensService.get({
        screen: 'GOODS_DETAIL',
        goods_sno: goodsSno,
        next_token: null,
      }),
    ).then(() => {
      const atomicComponents = client.getQueryData<ScreensReadModel>(
        queryKey.goodsAtomicComponents(goodsSno),
      );
      if (isNotNil(atomicComponents)) {
        const proxyComponents = atomicComponents.components.filter(
          component =>
            component.type.itemList === AtomicItemListType.Proxy &&
            // 기획전 이벤트 연관상품 off 실험: 해당 마켓 상품(176) 외 proxy 는 prefetch도 하지 않는다.
            (!sameMarketRecommendOnly ||
              isSameMarketProxyComponent(component)),
        );
        proxyComponents.map(component => {
          const requestUrl = component.entity.body.item.url;
          return prefetchIfAbsent(
            [
              ...sharedQueryKey.proxy(instanceId, requestUrl),
              { ably_only_log: {}, analytics: {} },
            ],
            () => screensService.getProxyComponents({ url: requestUrl }),
          );
        });
      }
    });
  }, [
    prefetchIfAbsent,
    client,
    goodsSno,
    instanceId,
    sameMarketRecommendOnly,
  ]);

  const prefetchSecondary = useCallback(() => {
    prefetchIfAbsent(queryKey.goodsDiscount(goodsSno, fixedCouponSno), () =>
      goodsService.getGoodsDiscounts(goodsSno, fixedCouponSno),
    );
    prefetchIfAbsent(queryKey.goodsCoupon(goodsSno, fixedCouponSno), () =>
      goodsService.getGoodsCoupon(goodsSno, fixedCouponSno),
    );
    // VIEW_GOODS_DETAIL 게이트 대상. 프리로드하지 않으면 content 마운트(runAfterInteractions)
    // 이후에야 요청이 시작돼, review 응답을 기다리는 동안 이탈하면 VIEW 가 누락됐다.
    prefetchIfAbsent(queryKey.goodsReviewSummary(goodsSno), () =>
      goodsService.getGoodsReviewSummary(goodsSno),
    );
    prefetchIfAbsent(queryKey.goodsOptionsBrief(goodsSno), () =>
      goodsService.getGoodsOptionsBrief(goodsSno),
    );
    prefetchIfAbsent(queryKey.goodsOptions(goodsSno, { depth: 1 }), () =>
      goodsService.getGoodsOptions(goodsSno, { depth: 1 }),
    );
    // 기획전 이벤트 연관상품 off 실험: 커버 연관(유사) 상품은 prefetch도 하지 않는다.
    if (!sameMarketRecommendOnly) {
      prefetchIfAbsent(queryKey.similarGoods(goodsSno), () =>
        goodsService.getSimilarGoods(goodsSno),
      );
    }
  }, [
    prefetchIfAbsent,
    goodsSno,
    fixedCouponSno,
    sameMarketRecommendOnly,
  ]);

  useEffect(() => {
    prefetchCritical();
    setSafeTimeout(prefetchSecondary, 0);
  }, [prefetchCritical, prefetchSecondary, setSafeTimeout]);
};
